/**
 * Release status a run's KPI is in. The backend still only returns a `client_facing` boolean
 * (see DEV-23639) — FOR_REVIEW joins once the backend enum + one-per-KPI enforcement exist.
 * No client-side mock of that third state ships here.
 */
export const ReleaseStatus = {
  UNMARKED: 'unmarked',
  CLIENT_FACING: 'client_facing'
} as const;
export type ReleaseStatusType = (typeof ReleaseStatus)[keyof typeof ReleaseStatus];

// Section headers for the run dropdown. This is the spec/PoC's own wording — deliberately not
// rMMM's pre-existing 'Admin-Only' string, so this is the vocabulary later tickets generalizing
// to Brand Equity/Geo should reuse rather than diverging further.
export const ReleaseSection: Record<ReleaseStatusType, string> = {
  [ReleaseStatus.CLIENT_FACING]: 'Client-facing',
  [ReleaseStatus.UNMARKED]: 'Unmarked (dev)'
};

// Sentinel option values for the run menu's toggle rows.
export const DEV_TOGGLE_VALUE = '__dev_toggle__';
export const OLDER_RUNS_VALUE = '__older_runs__';
export const OLDER_DEV_RUNS_VALUE = '__older_dev_runs__';
// Runs each release group lists (client-facing: Current + 2 older) before the rest go behind its
// "Show N older …" row.
export const INLINE_RUNS = 3;
// Untitled section holding the dev-runs toggle row at the top of the run menu.
export const DEV_TOGGLE_SECTION = 'dev-toggle';

// A single run scoped to one KPI, with that KPI's release status resolved.
export interface ModelRun {
  // Identifies the run when `refreshDate` can't (e.g. HROAS runs, keyed by run_id). Falls back to
  // `refreshDate`; read it through `runKey`.
  id?: string;
  kpi: string;
  branch: string;
  refreshDate: string; // ISO date or timestamp; identifies the run within its KPI
  releaseStatus: ReleaseStatusType;
  isCurrent: boolean; // the newest client-facing run for this KPI
  label: string; // pretty date + same-day (a)/(b) suffix
  // Set when the run can't be chosen in this context (e.g. it has no scenario planning model).
  // The menu shows the row disabled with this text as its tooltip.
  disabledReason?: string;
}

// A KPI paired with the runs that produced it — the KPI-first pivot of the run list.
export interface KpiRuns {
  value: string;
  label: string;
  runs: ModelRun[];
}

// The value that identifies a run in the selector's menu and selection.
export const runKey = (run: ModelRun): string => run.id ?? run.refreshDate;
